// Single shared WebSocket for the messenger (one per logged-in tab).
// Layout opens it; any component can subscribe to server events.
// Built to survive phones: liveness heartbeat, exponential reconnect,
// wake-up on foreground/online, and silent token refresh on 4401.
import api, { wsUrlFor } from "@/lib/api";

const listeners = new Set();
let socket = null;
let closed = true;
let retryTimer = null;
let hbTimer = null;
let lastSeen = 0;
let attempts = 0;
let refreshing = false;

const PING_MS = 20000;
const DEAD_MS = 45000;

const wsUrl = () => wsUrlFor("/api/messages/ws");

function emit(msg) {
  listeners.forEach((fn) => { try { fn(msg); } catch (e) { console.warn("messages listener failed", e); } });
}

function stopHeartbeat() { clearInterval(hbTimer); hbTimer = null; }

function killSocket() {
  const s = socket;
  socket = null;
  stopHeartbeat();
  if (!s) return;
  if (s.readyState === 0) { s.onmessage = null; s.onopen = () => s.close(); return; }
  if (s.readyState === 1) { try { s.close(); } catch (e) { console.warn("messages ws close failed", e); } }
}

function scheduleRetry(ms) {
  clearTimeout(retryTimer);
  const delay = ms ?? Math.min(15000, 1000 * 2 ** Math.min(attempts++, 4));
  retryTimer = setTimeout(connectMessagesSocket, delay);
}

function startHeartbeat(s) {
  stopHeartbeat();
  hbTimer = setInterval(() => {
    if (socket !== s) { stopHeartbeat(); return; }
    if (Date.now() - lastSeen > DEAD_MS) { killSocket(); scheduleRetry(0); return; }
    if (s.readyState === 1) s.send("ping");
  }, PING_MS);
}

async function handleUnauthorized() {
  killSocket();
  if (refreshing) return;
  refreshing = true;
  try {
    await api.post("/auth/refresh");
    scheduleRetry(300);
  } catch {
    closed = true; // session is really gone; Layout will redirect to login
  } finally {
    refreshing = false;
  }
}

export function connectMessagesSocket() {
  if (socket && socket.readyState <= 1) return;
  closed = false;
  clearTimeout(retryTimer);
  let s;
  try {
    s = new WebSocket(wsUrl());
  } catch {
    scheduleRetry();
    return;
  }
  socket = s;
  s.onopen = () => { attempts = 0; lastSeen = Date.now(); startHeartbeat(s); };
  s.onmessage = (ev) => {
    lastSeen = Date.now();
    let msg;
    try { msg = JSON.parse(ev.data); } catch { return; }
    if (msg.type === "error" && msg.code === "unauthorized") { handleUnauthorized(); return; }
    emit(msg);
  };
  s.onclose = () => {
    if (socket !== s) return; // superseded
    socket = null;
    stopHeartbeat();
    if (!closed) scheduleRetry();
  };
  s.onerror = () => {};
}

export function disconnectMessagesSocket() {
  closed = true;
  clearTimeout(retryTimer);
  killSocket();
}

export function onMessagesEvent(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function sendMessagesSocket(obj) {
  if (socket && socket.readyState === 1) socket.send(JSON.stringify(obj));
}

/** App came back to the foreground / network returned: verify the pipe and
 *  tell subscribers to refetch anything they may have missed. */
function wake() {
  if (closed) return;
  const stale = socket && socket.readyState === 1 && Date.now() - lastSeen > DEAD_MS;
  if (!socket || socket.readyState > 1 || stale) {
    killSocket();
    clearTimeout(retryTimer);
    attempts = 0;
    connectMessagesSocket();
  } else if (socket.readyState === 1) {
    socket.send("ping");
  }
  emit({ type: "resume" });
}

if (typeof window !== "undefined") {
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") wake(); });
  window.addEventListener("online", wake);
  window.addEventListener("pageshow", wake);
}
